import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { ShieldCheck, Lock } from "lucide-react";

export default function PortalLogin() {
  const { login } = useCustomerAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Client Portal — Login | Tech Alpha SSL" description="Log in to your Tech Alpha SSL client portal." />
      <section className="min-h-[calc(100vh-4rem)] grid place-items-center py-16">
        <div className="w-full max-w-md container-page">
          <div className="card-elevated p-8">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-gradient-to-br from-[#1565FF] to-[#4FC3F7] text-white">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div>
                <h1 className="text-lg font-semibold tracking-tight">Client Portal</h1>
                <p className="text-xs text-muted-foreground">Tech Alpha SSL</p>
              </div>
            </div>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="portal-login-form">
              <div>
                <label className="text-sm font-medium">Email</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="portal-login-email"
                  className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm outline-none focus:border-[#1565FF]" />
              </div>
              <div>
                <label className="text-sm font-medium">Password</label>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="portal-login-password"
                  className="mt-1 w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm outline-none focus:border-[#1565FF]" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="portal-login-error">{error}</p>}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="portal-login-submit">
                <Lock className="h-4 w-4" /> {loading ? "Signing in..." : "Sign in"}
              </button>
              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{" "}
                <Link to="/portal/signup" className="text-[#1565FF] font-medium" data-testid="portal-login-goto-signup">Create one</Link>
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
